import React, { useState, useMemo, useEffect } from 'react';
import {
  CalendarClock, CalendarPlus, Pencil, X as XIcon, Search,
  ChevronDown, ChevronRight, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { usePatientSessionsQuery } from '../../api/reportsApi';
import { useInfiniteQuery } from '../../hooks/useInfiniteQuery';
import { usePatientSessionSummaryQuery } from '../../api/sessionPackagesApi';
import {
  useCreateSessionMutation, useUpdateSessionMutation,
} from '../../api/sessionsApi';
import { useUpdatePatientSessionsMutation } from '../../api/patientsApi';
import { useListAppointmentsQuery, useExtendAppointmentMutation } from '../../api/appointmentsApi';
import { useListTherapistsQuery } from '../../api/usersApi';
import { useListTreatmentTypesQuery } from '../../api/treatmentApi';
import { SESSION_STATUS, APPOINTMENT_STATUS } from '../../utils/status';
import DateRangePicker, { useDateRange } from '../../components/DateRangePicker';
import { apiErrorMessage } from '../../utils/apiError';
import { isImported, cleanNotes } from '../../utils/imported';
import { doctorDisplayName } from '../appointments/doctorColors';
import { procedureLabel } from '../appointments/appointmentStatus';
import RenewalPitchModal, { isRenewalPitchRequired } from '../patients/RenewalPitchModal';
import './sessions.css';

const fmtINR = (v) => (v == null ? '—' : new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const pad = (n) => String(n).padStart(2, '0');
const toDateInput = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeInput = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const num = (v) => (v === '' || v == null ? null : Number(v));

// The 5 exact session statuses + their filter keys and badge colours.
const SESSION_STATUS_FILTERS = [
  { key: 'new', label: 'New Patient' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'followup', label: 'Follow-Ups' },
  { key: 'incoming', label: 'Incoming Appointment' },
  { key: 'noshow', label: 'No-Show' },
];
const SESSION_STATUS_BADGE = {
  'Incoming Appointment': 'b-green',
  'No-Show': 'b-red',
  'Follow-Up': 'b-amber',
  'Inactive': 'b-gray',
  'New Patient': 'b-blue',
};
const PAGE_SIZE = 15;

export default function SessionsPage() {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');   // '' | new | inactive | followup | incoming | noshow
  const [sortBy, setSortBy] = useState('code');
  const [sortDesc, setSortDesc] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // Temporal window: status signals (incoming appts, last appointment) bind to this range.
  // Default 'custom' with empty dates = no window (all time).
  const dr = useDateRange('custom');

  // Debounce the search box so we don't hit the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const filter = useMemo(() => ({
    sortBy, sortDesc,
    search: search || undefined,
    status: status || undefined,
    from: dr.range.from || undefined,
    to: dr.range.to || undefined,
  }), [sortBy, sortDesc, search, status, dr.range.from, dr.range.to]);

  // Session history is aggregated from the Appointment/Session data on the server, which also
  // computes the single 5-state status per patient (Incoming > No-Show > Follow-Up > Inactive > New).
  const { items: patients, total: totalCount, hasMore, isFetching, sentinelRef } =
    useInfiniteQuery(usePatientSessionsQuery, filter, { pageSize: PAGE_SIZE });

  const setSort = (col) => {
    if (sortBy === col) setSortDesc((d) => !d);
    else { setSortBy(col); setSortDesc(false); }
  };
  const SortTh = ({ col, children, style }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none', ...style }} onClick={() => setSort(col)}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        {sortBy === col && (sortDesc ? <ArrowDown size={12} /> : <ArrowUp size={12} />)}
      </span>
    </th>
  );

  const toggle = (id) => setExpandedId((cur) => (cur === id ? null : id));

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb"><span>Home</span><span className="bc-sep">/</span><span className="bc-cur">Sessions</span></div>
        <div className="page-header-row">
          <div>
            <h1>Sessions</h1>
            <div className="page-header-sub">Session history aggregated from appointments. Each patient shows a single status; click a row for full history.</div>
          </div>
        </div>
      </div>

      {/* Filters — the status dropdown is the 5 session categories only. */}
      <div className="sx-filters">
        <div className="sx-search">
          <Search size={15} className="sx-search-icon" />
          <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search by name, phone or patient ID…" />
        </div>
        <select className="form-control sx-filter-sel" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {SESSION_STATUS_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <span title="Bind status filters to a date range (e.g. incoming appointments within X–Y)">
          <DateRangePicker
            preset={dr.preset} onPresetChange={dr.setPreset}
            customFrom={dr.customFrom} customTo={dr.customTo} onCustomChange={dr.onCustomChange}
            size="sm"
          />
        </span>
      </div>

      <div className="tbl-wrap sx-patient-tbl">
        <table className="sx-ledger">
          <thead>
            <tr>
              <th style={{ width: 36 }}></th>
              <SortTh col="name">Patient</SortTh>
              <SortTh col="code" style={{ width: 110 }}>ID</SortTh>
              <SortTh col="total" style={{ width: 120 }}>Total sessions</SortTh>
              <th style={{ width: 120 }}>Sessions took</th>
              <SortTh col="remaining" style={{ width: 120 }}>Remaining</SortTh>
              <SortTh col="last" style={{ width: 140 }}>Last session</SortTh>
              <SortTh col="status" style={{ width: 175 }}>Status</SortTh>
            </tr>
          </thead>
          <tbody>
            {patients.length === 0 && !isFetching && (
              <tr><td colSpan={8} className="td-muted" style={{ textAlign: 'center', padding: 28 }}>No patients match these filters.</td></tr>
            )}
            {patients.map((p) => (
              <PatientSessionRow key={p.patientId} item={p} expanded={expandedId === p.patientId} onToggle={toggle} />
            ))}
            {isFetching && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 14 }}>
                <div className="skeleton" style={{ height: 16, maxWidth: 220, margin: '0 auto' }} />
              </td></tr>
            )}
          </tbody>
        </table>
        {/* Infinite-scroll sentinel — loads the next page of patients as it enters view. */}
        {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
      </div>

      <div className="sx-pager" style={{ justifyContent: 'center' }}>
        <div className="td-muted" style={{ fontSize: '0.8rem' }}>
          Showing {patients.length.toLocaleString('en-IN')} of {totalCount.toLocaleString('en-IN')} patient{totalCount === 1 ? '' : 's'}
          {hasMore ? ' — scroll for more' : ''}
        </div>
      </div>
    </>
  );
}

function PatientSessionRow({ item, expanded, onToggle }) {
  const badge = SESSION_STATUS_BADGE[item.sessionStatus] || 'b-gray';
  // Inline-edit for "Total sessions" — mirrors the click-to-edit pattern used on the
  // Billing register. Click pencil → input → Enter/blur saves, Escape cancels. The
  // edit hits PATCH /patients/{id}/sessions which sets Patient.TotalSessions and the
  // row refetches automatically via the Patient LIST RTK Query tag.
  const [editingTotal, setEditingTotal] = useState(false);
  const [draftTotal, setDraftTotal] = useState(String(item.totalSessions ?? 0));
  const [saveTotal, { isLoading: savingTotal }] = useUpdatePatientSessionsMutation();
  useEffect(() => { setDraftTotal(String(item.totalSessions ?? 0)); }, [item.totalSessions]);

  const commitTotal = async () => {
    const next = Math.max(0, Math.round(Number(draftTotal) || 0));
    setEditingTotal(false);
    if (next === (item.totalSessions ?? 0)) return;
    try {
      await saveTotal({ id: item.patientId, totalSessions: next }).unwrap();
      toast.success('Total sessions updated');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not update total sessions'));
      setDraftTotal(String(item.totalSessions ?? 0));
    }
  };
  const cancelTotal = () => {
    setDraftTotal(String(item.totalSessions ?? 0));
    setEditingTotal(false);
  };

  return (
    <React.Fragment>
      <tr className={`sx-row-link ${expanded ? 'sx-row-open' : ''}`} onClick={() => !editingTotal && onToggle(item.patientId)}>
        <td className="td-muted">{expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</td>
        <td>
          <span className="sx-proc-name">{item.patientName}</span>
          {item.sessionStatus === 'New Patient' && <span className="sx-new-badge" title="New patient">(N)</span>}
        </td>
        <td className="td-muted">{item.patientCode}</td>
        <td
          className="td-muted sx-editable-cell"
          onClick={(e) => { e.stopPropagation(); if (!editingTotal) setEditingTotal(true); }}
          title="Click to edit total sessions"
        >
          {editingTotal ? (
            <input
              type="number"
              min="0"
              step="1"
              autoFocus
              className="form-control sx-inline-input"
              value={draftTotal}
              disabled={savingTotal}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setDraftTotal(e.target.value)}
              onBlur={commitTotal}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitTotal(); }
                else if (e.key === 'Escape') { e.preventDefault(); cancelTotal(); }
              }}
              onFocus={(e) => e.target.select()}
            />
          ) : (
            <span className="sx-editable-value">
              {item.totalSessions ?? 0}
              <Pencil size={11} className="sx-editable-pencil" />
            </span>
          )}
        </td>
        <td className="td-muted">{item.completedSessions}</td>
        <td className="td-muted">{item.remainingSessions}</td>
        <td className="td-muted">{fmtDate(item.lastSessionAt)}</td>
        <td><span className={`badge ${badge}`}>{item.sessionStatus}</span></td>
      </tr>
      {expanded && (
        <tr className="sx-detail-row">
          <td colSpan={8}>
            <PatientSessionHistory patient={{ id: item.patientId, fullName: item.patientName }} />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

function PatientSessionHistory({ patient }) {
  const pid = patient.id;
  // Session history is read straight from the Appointments section (the source of truth — each
  // appointment is one session). Sorted newest-first.
  const { data: apptPage, isFetching } = useListAppointmentsQuery(
    { patientId: pid, pageSize: 200, sortBy: 'scheduledAt', sortDesc: true }, { skip: !pid });
  // Packages power the "Add session" package dropdown.
  const { data: summary } = usePatientSessionSummaryQuery(pid, { skip: !pid });
  const packages = summary?.packages ?? [];
  const [sessionModal, setSessionModal] = useState(null);

  const appointments = useMemo(() => {
    const items = apptPage?.items ?? [];
    return [...items].sort((a, b) => new Date(b.scheduledAt) - new Date(a.scheduledAt));
  }, [apptPage]);

  return (
    <div className="sx-detail">
      <div className="sx-section">
        <div className="sx-section-head">
          <h3><CalendarClock size={15} /> Session history <span className="sx-count">{appointments.length}</span></h3>
          <button className="btn btn-primary btn-sm" onClick={() => setSessionModal({ create: true })}><CalendarPlus size={14} /> Add session</button>
        </div>
        {isFetching && appointments.length === 0 ? (
          <div className="skeleton" style={{ height: 120 }} />
        ) : appointments.length === 0 ? (
          <div className="sx-empty">No appointments recorded for this patient yet.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="sx-ledger">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th><th style={{ width: 170 }}>When</th>
                  <th>Procedure / Package</th><th>Doctor</th><th>Status</th><th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {appointments.map((a, idx) => {
                  const meta = APPOINTMENT_STATUS[a.status] || { label: a.status, color: 'b-gray' };
                  const imported = isImported(a.notes);
                  // Procedure inherited from the package line item this appointment drew from.
                  // Same single label the rest of the CRM uses; completedPackageName remains the
                  // muted sub-line below, so it is not part of the headline resolution.
                  const procedure = procedureLabel(a) || a.completedPackageName;
                  return (
                    <tr key={a.id}>
                      <td className="td-muted">#{appointments.length - idx}</td>
                      <td className="td-muted">{imported ? fmtDate(a.scheduledAt) : fmtDateTime(a.scheduledAt)}</td>
                      <td>
                        <span className="sx-proc-name">{procedure || 'Session'}</span>
                        {a.completedPackageName && (
                          <div className="td-muted" style={{ fontSize: '0.72rem' }}>
                            {a.completedPackageName}{a.completedSessionNumber ? ` · session #${a.completedSessionNumber}` : ''}
                          </div>
                        )}
                      </td>
                      <td>{a.assignedUserName ? doctorDisplayName(a.assignedUserName) : '—'}</td>
                      <td><span className={`badge ${meta.color}`}>{meta.label}</span></td>
                      <EditableNotesCell appointment={a} />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {sessionModal && <SessionModal patientId={pid} session={sessionModal.session} packages={packages} onClose={() => setSessionModal(null)} />}
    </div>
  );
}

// Inline-edit for an Appointment's free-text Notes — click cell, type, Enter/blur saves,
// Escape cancels. Hits PUT /appointments/:id (extendAppointment) which already
// patches the calendar cache + invalidates the LIST tag so this row refetches.
function EditableNotesCell({ appointment: a }) {
  const note = cleanNotes(a.notes);
  const imported = isImported(a.notes);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note);
  const [save, { isLoading: saving }] = useExtendAppointmentMutation();
  useEffect(() => { setDraft(note); }, [note]);

  const commit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next === note) return;
    // Preserve the [IMPORTED] marker so the "Imported" chip + import filters
    // keep working; everything else is the staff-editable free-text body.
    const merged = (imported ? '[IMPORTED]' : '') + (next ? ' ' + next : '');
    try {
      await save({
        id: a.id,
        assignedUserId: a.assignedUserId,
        scheduledAt: a.scheduledAt,
        durationMinutes: a.durationMinutes ?? 60,
        notes: merged.trim() || null,
      }).unwrap();
      toast.success('Notes updated');
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Could not update notes'));
      setDraft(note);
    }
  };
  const cancel = () => { setDraft(note); setEditing(false); };

  return (
    <td
      className="td-muted sx-editable-cell"
      style={{ fontSize: '0.78rem', minWidth: 160 }}
      onClick={() => { if (!editing) setEditing(true); }}
      title="Click to edit notes"
    >
      {editing ? (
        <input
          type="text"
          autoFocus
          className="form-control sx-inline-input"
          value={draft}
          disabled={saving}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          onFocus={(e) => e.target.select()}
        />
      ) : (
        <span className="sx-editable-value">
          {note || (imported ? 'Imported' : '—')}
          <Pencil size={11} className="sx-editable-pencil" />
        </span>
      )}
    </td>
  );
}

// ── Session add/edit (ledger) ─────────────────────────────────────────
function SessionModal({ patientId, session, packages, onClose }) {
  const editing = !!session;
  const base = session ? new Date(session.scheduledAt) : new Date();
  const { data: therapists = [] } = useListTherapistsQuery();
  const { data: treatments = [] } = useListTreatmentTypesQuery();
  const [createSession, { isLoading: creating }] = useCreateSessionMutation();
  const [updateSession, { isLoading: updating }] = useUpdateSessionMutation();
  const [form, setForm] = useState({
    date: toDateInput(base), time: toTimeInput(base),
    durationMinutes: session?.durationMinutes ?? 45,
    status: session?.status ?? 'Scheduled',
    therapistUserId: session?.therapistUserId ?? '',
    treatmentTypeId: session?.treatmentTypeId ?? '',
    sessionPackageId: session?.sessionPackageId ?? '',
    sessionNumber: session?.sessionNumber ?? '',
    amountCharged: session?.amountCharged ?? '',
    notes: session?.notes ?? '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [pitchPrompt, setPitchPrompt] = useState(false);

  const doSave = async () => {
    const dt = new Date(form.date); const [hh, mm] = (form.time || '00:00').split(':');
    dt.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
    const body = {
      scheduledAt: dt.toISOString(),
      durationMinutes: Number(form.durationMinutes) || 45,
      status: form.status,
      therapistUserId: form.therapistUserId || null,
      treatmentTypeId: form.treatmentTypeId || null,
      sessionPackageId: form.sessionPackageId || null,
      sessionNumber: num(form.sessionNumber),
      amountCharged: num(form.amountCharged),
      notes: form.notes?.trim() || null,
    };
    if (editing) { await updateSession({ id: session.id, ...body }).unwrap(); toast.success('Session updated'); }
    else { await createSession({ patientId, ...body }).unwrap(); toast.success('Session added'); }
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await doSave();
    } catch (err) {
      // Compulsory renewal-pitch guard on the 2nd-to-last session → capture it, then retry.
      if (isRenewalPitchRequired(err)) { setPitchPrompt(true); return; }
      toast.error(apiErrorMessage(err, 'Could not save session'));
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      {pitchPrompt && (
        <RenewalPitchModal
          patientId={patientId}
          onClose={() => setPitchPrompt(false)}
          onSaved={async () => {
            setPitchPrompt(false);
            try { await doSave(); } catch (err) { toast.error(apiErrorMessage(err, 'Could not save session')); }
          }}
        />
      )}
      <form className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header"><h2>{editing ? 'Edit session' : 'Add session'}</h2><button type="button" className="modal-close" onClick={onClose}><XIcon size={18} /></button></div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label className="form-label">Date</label><input className="form-control" type="date" value={form.date} onChange={(e) => set('date', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Time</label><input className="form-control" type="time" value={form.time} onChange={(e) => set('time', e.target.value)} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Duration (min)</label><input className="form-control" type="number" inputMode="numeric" value={form.durationMinutes} onChange={(e) => set('durationMinutes', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Status</label>
              <select className="form-control" value={form.status} onChange={(e) => set('status', e.target.value)}>
                {Object.entries(SESSION_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">Practitioner</label>
              <select className="form-control" value={form.therapistUserId} onChange={(e) => set('therapistUserId', e.target.value)}>
                <option value="">— Unassigned —</option>
                {therapists.map((t) => <option key={t.id} value={t.id}>{doctorDisplayName(t.fullName)}</option>)}
              </select></div>
            <div className="form-group"><label className="form-label">Procedure</label>
              <select className="form-control" value={form.treatmentTypeId} onChange={(e) => set('treatmentTypeId', e.target.value)}>
                <option value="">— None —</option>
                {treatments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select></div>
          </div>
          <div className="form-row">
            {packages.length > 0 && (
              <div className="form-group"><label className="form-label">Package</label>
                <select className="form-control" value={form.sessionPackageId} onChange={(e) => set('sessionPackageId', e.target.value)}>
                  <option value="">— None —</option>
                  {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></div>
            )}
            <div className="form-group"><label className="form-label">Session #</label><input className="form-control" type="number" inputMode="numeric" value={form.sessionNumber} onChange={(e) => set('sessionNumber', e.target.value)} placeholder="auto" /></div>
            <div className="form-group"><label className="form-label">Amount (₹)</label><input className="form-control" type="number" inputMode="decimal" value={form.amountCharged} onChange={(e) => set('amountCharged', e.target.value)} placeholder="optional" /></div>
          </div>
          <div className="form-group"><label className="form-label">Treatment notes</label><textarea className="form-control" rows={3} value={form.notes} onChange={(e) => set('notes', e.target.value)} /></div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={creating || updating}>{creating || updating ? 'Saving…' : (editing ? 'Save session' : 'Add session')}</button>
        </div>
      </form>
    </div>
  );
}
